import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Sparkle, Copy, PaperPlaneTilt, Trash } from "@phosphor-icons/react";

/**
 * DraftUpdateModal — generates + reviews an AI-drafted landlord status update.
 *
 * Props:
 *   open (bool)                       — visibility
 *   onClose ()                        — dismiss
 *   jobId (str, optional)             — job to summarize
 *   landlordId (str, optional)        — or a landlord for portfolio-wide draft
 *   defaultQuestion (str, optional)   — pre-fill landlord's pasted question
 *   onSent (draft)                    — callback after send/mark-sent
 */
export default function DraftUpdateModal({
  open, onClose, jobId, landlordId, defaultQuestion = "", onSent,
}) {
  const [question, setQuestion] = useState(defaultQuestion);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  useEffect(() => { setQuestion(defaultQuestion); }, [defaultQuestion]);
  useEffect(() => { if (!open) { setDraft(null); setSubject(""); setBody(""); } }, [open]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/updates/draft", {
        job_id: jobId || null, landlord_id: landlordId || null,
        landlord_question: question, tone: "professional",
      }, { timeout: 60000 });
      setDraft(data);
      setSubject(data.subject);
      setBody(data.body);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed to draft"));
    } finally { setBusy(false); }
  };

  const saveEdits = async () => {
    if (!draft) return null;
    if (subject === draft.subject && body === draft.body) return draft;
    try {
      const { data } = await api.patch(`/updates/drafts/${draft.id}`, { subject, body });
      setDraft(data);
      return data;
    } catch (e) {
      toast.error("Failed to save edits");
      return null;
    }
  };

  const sendViaResend = async () => {
    setSending(true);
    try {
      const latest = await saveEdits();
      if (!latest) { setSending(false); return; }
      if (!latest.landlord_email) {
        toast.error("No landlord email on file — use Copy to Clipboard instead");
        setSending(false); return;
      }
      await api.post(`/updates/drafts/${draft.id}/send`);
      toast.success(`Sent to ${latest.landlord_email}`);
      onSent && onSent(latest);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Send failed — try Copy to Clipboard"));
    } finally { setSending(false); }
  };

  const copyAndMarkSent = async () => {
    setSending(true);
    try {
      const latest = await saveEdits();
      const text = `Subject: ${subject}\n\n${body}`;
      await navigator.clipboard.writeText(text);
      await api.post(`/updates/drafts/${draft.id}/mark-sent`);
      toast.success("Copied to clipboard · marked as sent");
      onSent && onSent(latest);
      onClose();
    } catch (e) {
      toast.error("Copy failed — please select the text manually");
    } finally { setSending(false); }
  };

  const discard = async () => {
    if (!draft) { onClose(); return; }
    if (!window.confirm("Discard this draft?")) return;
    try { await api.delete(`/updates/drafts/${draft.id}`); } catch {}
    onClose();
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-[80] flex items-end sm:items-center justify-center p-2 sm:p-4" data-testid="draft-update-modal">
      <div className="bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto border-4 border-slate-900 shadow-[6px_6px_0_#0f172a]">
        <div className="p-4 bg-fuchsia-600 text-white flex items-start justify-between gap-2 sticky top-0">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest opacity-80">AI Landlord Update</div>
            <div className="text-lg font-black uppercase tracking-widest">🪄 Draft an update</div>
          </div>
          <button onClick={onClose} data-testid="draft-close-btn" className="p-1 hover:bg-fuchsia-700"><X size={18}/></button>
        </div>

        <div className="p-4 space-y-4">
          {!draft && (
            <>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Landlord's specific question (optional)</label>
                <textarea data-testid="draft-question-input"
                  value={question} onChange={e => setQuestion(e.target.value)}
                  rows={4} maxLength={4000}
                  placeholder="Paste the landlord's email question here, or leave blank for a proactive status update…"
                  className="mt-1 w-full p-3 border-2 border-slate-300 focus:border-fuchsia-500 outline-none text-sm"/>
                <div className="text-[10px] text-slate-500 mt-1">Claude will build a targeted reply from the job's history + any question you paste.</div>
              </div>
              <button onClick={generate} disabled={busy} data-testid="draft-generate-btn"
                className="w-full h-12 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase tracking-widest text-sm disabled:opacity-50 shadow-[3px_3px_0_#0f172a] inline-flex items-center justify-center gap-2">
                <Sparkle weight="fill" size={16}/> {busy ? "Claude is drafting…" : "Generate Draft"}
              </button>
            </>
          )}

          {draft && (
            <>
              {draft.landlord_email ? (
                <div className="p-2 bg-emerald-50 border-2 border-emerald-500 text-xs" data-testid="draft-to-line">
                  <span className="font-black uppercase tracking-widest text-emerald-800 text-[10px]">To:</span>{" "}
                  <b>{draft.landlord_name}</b> &lt;{draft.landlord_email}&gt;
                </div>
              ) : (
                <div className="p-2 bg-amber-50 border-2 border-amber-500 text-xs" data-testid="draft-no-email-warn">
                  ⚠️ No landlord email on file. Send via Resend disabled — use Copy to Clipboard.
                </div>
              )}

              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Subject</label>
                <input data-testid="draft-subject-input"
                  value={subject} onChange={e => setSubject(e.target.value)}
                  maxLength={200}
                  className="mt-1 w-full h-10 px-3 border-2 border-slate-300 focus:border-fuchsia-500 outline-none text-sm font-medium"/>
              </div>

              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Body — edit freely</label>
                <textarea data-testid="draft-body-input"
                  value={body} onChange={e => setBody(e.target.value)}
                  rows={14} maxLength={8000}
                  className="mt-1 w-full p-3 border-2 border-slate-300 focus:border-fuchsia-500 outline-none text-sm font-mono leading-relaxed"/>
                <div className="text-[10px] text-slate-500 mt-1">{body.length} / 8000 chars</div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <button onClick={discard} disabled={sending} data-testid="draft-discard-btn"
                  className="h-11 border-2 border-red-500 text-red-600 font-black uppercase tracking-widest text-[11px] hover:bg-red-50 disabled:opacity-50 inline-flex items-center justify-center gap-1">
                  <Trash size={12}/> Discard
                </button>
                <button onClick={copyAndMarkSent} disabled={sending} data-testid="draft-copy-btn"
                  className="h-11 border-2 border-slate-900 bg-white text-slate-900 font-black uppercase tracking-widest text-[11px] hover:bg-slate-100 disabled:opacity-50 inline-flex items-center justify-center gap-1">
                  <Copy size={12}/> {sending ? "…" : "Copy & Mark Sent"}
                </button>
                <button onClick={sendViaResend} disabled={sending || !draft.landlord_email} data-testid="draft-send-btn"
                  className="h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-[11px] disabled:opacity-40 shadow-[2px_2px_0_#0f172a] inline-flex items-center justify-center gap-1">
                  <PaperPlaneTilt weight="fill" size={12}/> {sending ? "Sending…" : "Send via Email"}
                </button>
              </div>
              <div className="text-[10px] text-slate-500 text-center">
                Send via Email uses Field Radar's managed inbox (Resend). Reply-To will be your admin email so responses come to you.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
